import { addWindowListener, noop } from '@pdcfrontendui/utils';
import type { Api } from '../api';
import { coerceToError } from './errors';
import type { CleanupFunction } from '../brands';
import { addErrorBoundaryListener } from '../components/ErrorBoundary';

/**
 * Too little, and we might log the same error twice.
 * Too much, and we might miss logging another error.
 */
const ERROR_BOUNDARY_TIMEOUT_MS = 100;

export function setupGlobalErrorLogger({
  api,
  logRetry,
  usesErrorBoundary,
}: {
  api: Pick<Api, 'log'>;
  logRetry?: number;
  /**
   * If an error boundary is used anywhere in the application.
   */
  usesErrorBoundary?: boolean;
}) {
  return addWindowListener(
    'error',
    (event) => {
      let loggedByErrorBoundary = false;
      const cleanup = usesErrorBoundary
        ? addErrorBoundaryListener(() => {
            // Do a fair guess that whatever the error boundary has caught is related to the thrown error.
            loggedByErrorBoundary = true;
          })
        : null;
      // Global error logger catches the error first.
      // Then, an error boundary may catch the error.
      // Since the error boundary provides more information about the error, we prefer that the error boundary logs the error.
      // Yes, it's pretty hacky.
      setTimeout(
        () => {
          cleanup?.();
          // Don't log if the error (or a derived one) was already logged by an error boundary
          if (!loggedByErrorBoundary) {
            api
              .log(
                coerceToError(event.error),
                logRetry !== undefined
                  ? {
                      allowErrors: false,
                      retry: logRetry,
                    }
                  : undefined
              )
              .catch(noop);
          }
        },
        usesErrorBoundary ? ERROR_BOUNDARY_TIMEOUT_MS : 0
      );
    },
    { capture: true }
  ) as CleanupFunction;
}
